import { useEffect, useRef } from "react";
import { useScrollStore } from "@/common/store/scrollStore";

const THRESHOLD = 0.01;

interface IUseInfiniteScrollArgs {
  fetchMore: () => void | Promise<void>;
  hasMore: boolean;
  root?: string;
}

export const useInfiniteScroll = ({ fetchMore, hasMore, root }: IUseInfiniteScrollArgs) => {
  const scrollElement = useScrollStore(state => state.scrollElement);
  const spyElement = useRef<HTMLDivElement | null>(null);
  const observer = useRef<IntersectionObserver | null>(null);
  const handleNext = useRef<() => void | Promise<void>>(fetchMore);
  const isLoading = useRef(false);

  const startObserve = () => {
    if (!spyElement.current || !observer.current) {
      return;
    }

    observer.current.observe(spyElement.current);
  };

  const clearObserve = () => {
    observer.current?.disconnect();
  };

  const loadMore = async () => {
    if (isLoading.current) {
      return;
    }

    isLoading.current = true;

    try {
      await Promise.resolve(handleNext.current());
    } catch {
      // ignore error
    } finally {
      isLoading.current = false;
    }
  };

  const handleIntersection: IntersectionObserverCallback = entries => {
    entries.forEach(async entry => {
      if (!entry.isIntersecting || !hasMore || isLoading.current) {
        return;
      }
      clearObserve();
      await loadMore();
      if (hasMore) {
        startObserve();
      }
    });
  };

  useEffect(() => {
    const resolvedRoot = root ? document.querySelector(root) : scrollElement;

    observer.current = new IntersectionObserver(handleIntersection, {
      root: resolvedRoot,
      rootMargin: "0% 0% 60% 0%",
      threshold: THRESHOLD
    });

    if (hasMore) {
      startObserve();
    }

    return () => {
      clearObserve();
      observer.current = null;
    };
  }, [root, hasMore, scrollElement]);

  useEffect(() => {
    handleNext.current = fetchMore;
  }, [fetchMore]);

  return spyElement;
};
